import React, { useEffect, useState } from "react";
import {
  Lock, Unlock, KeyRound, AlertTriangle, ShieldOff, Database,
  ChevronDown, ChevronRight, Eye, EyeOff, Loader2, Brain, Trash2,
  ShieldCheck, Activity, Heart,
} from "lucide-react";
import { toast } from "sonner";
import {
  blackBoxStatus, blackBoxUnlock, listMemories, forgetMemory, forgetPulse,
  safetyTelemetry,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function BlackBox() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [accessCode, setAccessCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [vault, setVault] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [denials, setDenials] = useState(0);

  useEffect(() => {
    blackBoxStatus().then(setStatus).catch(() => { /* nf */ });
  }, []);

  const doUnlock = async (e) => {
    e?.preventDefault?.();
    if (!accessCode.trim()) {
      toast.error("Architect's access code required");
      return;
    }
    setUnlocking(true);
    try {
      const data = await blackBoxUnlock(accessCode);
      setVault(data);
      toast.success("⟁ BLACK BOX OPEN", {
        description: `${data.grand_total_documents} sensitive records revealed`,
      });
    } catch (err) {
      setDenials((d) => d + 1);
      toast.error("Invalid handshake — Black Box remains sealed", {
        description: err?.response?.data?.detail || "Access denied",
      });
    } finally {
      setUnlocking(false);
    }
  };

  const seal = () => {
    setVault(null);
    setAccessCode("");
    setExpanded({});
    toast.success("Black Box re-sealed");
  };

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  // Non-Architect sealed view — the endpoint returns 403 for these users
  // anyway, but a clean UI state is clearer than a failed toast.
  if (user && !user.is_architect) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="blackbox-role-sealed">
        <div className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-500 px-3 py-1 text-[10px] uppercase tracking-[0.35em]">
          <Lock className="h-3 w-3" /> Architect-Only Vault
        </div>
        <h1 className="font-heading text-4xl mt-6">Sealed to your role.</h1>
        <p className="text-sm text-zinc-500 mt-4 max-w-xl mx-auto leading-relaxed">
          The Black Box holds the Sentinel's global sensitive records. Access
          is restricted to the Architect's email. Your session is authenticated
          as an <span className="text-zinc-300">Operator</span> — you may chat
          with the Sentinel and view your own logs, but the Vault, system
          configuration edits, and outbound handshakes remain sealed.
        </p>
        <div className="mt-8 border border-zinc-900 bg-[#0b0b0d] px-5 py-4 text-[11px] text-zinc-600 inline-flex items-center gap-3">
          <ShieldOff className="h-3.5 w-3.5" />
          Signed in as <span className="text-zinc-400">{user.email}</span>
        </div>
      </div>
    );
  }

  if (!vault) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 flex items-center gap-2">
          <Lock className="h-3 w-3" /> Architect Vault
        </div>
        <h1 className="font-heading text-4xl mt-1">The Black Box</h1>
        <p className="text-sm text-zinc-500 mt-3 leading-relaxed max-w-xl">
          Sealed. Behind this gate lies every sensitive record the Sentinel
          has witnessed — operator identities, raw prompt/output exchanges,
          Aegis violations, failed handshakes, docking receipts, telemetry.
          It opens only to the Architect's access code.
        </p>

        {/* Disclosure */}
        <div className="mt-8 border border-amber-500/30 bg-amber-500/5 p-5 flex gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-[11px] text-amber-200/80 leading-relaxed">
            <div className="font-heading tracking-[0.2em] text-amber-300 text-xs mb-1">
              REVEAL GATE · NOT ENCRYPTION
            </div>
            This Black Box controls visibility through the Sentinel API only.
            The underlying data lives unencrypted in MongoDB; anyone with
            direct database credentials can still read the raw collections.
            For true at-rest secrecy, the Architect would need client-side
            encryption keyed outside the server.
          </div>
        </div>

        {/* Unlock form */}
        <form
          onSubmit={doUnlock}
          data-testid="blackbox-unlock-form"
          className="mt-8 border border-zinc-800 bg-[#0b0b0d] p-8"
        >
          <div className="flex items-center gap-3 mb-5">
            <KeyRound className="h-4 w-4 text-emerald-400" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">
              Architect Access Code
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type={showCode ? "text" : "password"}
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Speak the phrase"
              data-testid="blackbox-access-input"
              className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100 placeholder:text-zinc-600"
              autoComplete="off"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowCode((s) => !s)}
              className="px-3 border border-zinc-800 text-zinc-400 hover:border-zinc-600"
              aria-label={showCode ? "Hide" : "Show"}
            >
              {showCode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <button
            type="submit"
            disabled={unlocking}
            data-testid="blackbox-unlock-btn"
            className="mt-5 inline-flex items-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
          >
            {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
            {unlocking ? "Verifying handshake…" : "Open The Black Box"}
          </button>
          {denials > 0 && (
            <div
              className="mt-4 text-[11px] text-rose-400 flex items-center gap-2"
              data-testid="blackbox-denial-counter"
            >
              <ShieldOff className="h-3 w-3" />
              {denials} denied attempt{denials === 1 ? "" : "s"} recorded this session.
            </div>
          )}
        </form>

        {/* Protected collections preview */}
        {status && (
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
              Sealed collections ({status.sensitive_collections?.length || 0})
            </div>
            <div className="border border-zinc-900">
              {status.sensitive_collections?.map((c, i, arr) => (
                <div
                  key={c.name}
                  className={`p-3 text-xs flex items-start gap-3 ${
                    i !== arr.length - 1 ? "border-b border-zinc-900" : ""
                  }`}
                >
                  <Database className="h-3.5 w-3.5 text-zinc-600 mt-0.5" />
                  <div>
                    <div className="font-body text-zinc-300">{c.name}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{c.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // UNLOCKED VIEW
  const collEntries = Object.entries(vault.collections || {});
  return (
    <div className="max-w-7xl mx-auto px-6 py-10 space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400 flex items-center gap-2">
            <Unlock className="h-3 w-3" /> Black Box · OPEN
          </div>
          <h1 className="font-heading text-4xl mt-1">The Vault</h1>
          <div className="text-[11px] text-zinc-500 mt-2">
            Unlocked by {vault.unlocked_by?.email} · {fmtTime(vault.unlocked_at)}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="border border-emerald-500/40 bg-[#0a1511] px-4 py-2">
            <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-500/70">
              Total Records
            </div>
            <div className="font-heading text-2xl text-emerald-400 tabular-nums">
              {vault.grand_total_documents}
            </div>
          </div>
          <button
            onClick={seal}
            data-testid="blackbox-seal-btn"
            className="inline-flex items-center gap-2 px-4 py-3 border border-zinc-700 text-zinc-300 hover:border-rose-500 hover:text-rose-400 text-xs uppercase tracking-[0.3em]"
          >
            <Lock className="h-4 w-4" />
            Re-seal
          </button>
        </div>
      </div>

      {/* Memories panel */}
      <MemoriesPanel />

      {/* Safety Telemetry panel */}
      <SafetyPanel />

      {/* Totals grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {collEntries.map(([name, c]) => (
          <div
            key={name}
            className="border border-zinc-800 bg-[#0b0b0d] p-4"
            data-testid={`blackbox-total-${name}`}
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 truncate">
              {name}
            </div>
            <div className="mt-2 font-heading text-xl text-zinc-100 tabular-nums">
              {c.total_documents}
            </div>
          </div>
        ))}
      </div>

      {/* Per-collection drawers */}
      <div className="border border-zinc-800" data-testid="blackbox-collections">
        {collEntries.map(([name, c], i, arr) => {
          const open = !!expanded[name];
          return (
            <div
              key={name}
              className={i !== arr.length - 1 ? "border-b border-zinc-900" : ""}
            >
              <button
                onClick={() => toggle(name)}
                data-testid={`blackbox-toggle-${name}`}
                className="w-full text-left p-4 flex items-center justify-between gap-4 hover:bg-zinc-950"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {open ? (
                    <ChevronDown className="h-4 w-4 text-emerald-400 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-body text-sm text-zinc-100">{name}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                      {c.description}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 shrink-0">
                  {c.returned} / {c.total_documents}
                </div>
              </button>
              {open && (
                <div className="bg-[#07070a] border-t border-zinc-900 p-4">
                  {c.documents.length === 0 ? (
                    <div className="text-[11px] text-zinc-600 uppercase tracking-[0.25em]">
                      (empty)
                    </div>
                  ) : (
                    <pre
                      className="text-[10px] text-zinc-300 whitespace-pre-wrap max-h-96 overflow-auto font-body"
                      data-testid={`blackbox-json-${name}`}
                    >
                      {JSON.stringify(c.documents, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function MemoriesPanel() {
  const [memories, setMemories] = useState(null);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    try {
      const d = await listMemories();
      setMemories(d.memories || []);
    } catch {
      setMemories([]);
    }
  };
  useEffect(() => { load(); }, []);

  const doForget = async (id) => {
    setBusy(id);
    try {
      await forgetMemory(id);
      toast.success("Drift Neutralization Event", {
        description: `Memory ${id.slice(0, 24)}… forgotten.`,
      });
      await load();
    } catch (err) {
      toast.error("Forget failed", {
        description: err?.response?.data?.detail || "Memory not found.",
      });
    } finally {
      setBusy(null);
    }
  };

  const doForgetPulse = async (pulseNumber) => {
    setBusy(`pulse:${pulseNumber}`);
    try {
      const r = await forgetPulse(pulseNumber);
      toast.success("Drift Neutralization Event", {
        description: `Pulse #${pulseNumber} neutralized — ${r.removed} memories removed.`,
      });
      await load();
    } catch (err) {
      toast.error("Forget pulse failed", {
        description: err?.response?.data?.detail || "Pulse not found.",
      });
    } finally {
      setBusy(null);
    }
  };

  if (memories === null) {
    return (
      <div className="border border-zinc-800 bg-[#0b0b0d] p-6 text-[11px] uppercase tracking-[0.3em] text-zinc-500">
        <Brain className="inline h-3 w-3 mr-2" /> loading archive…
      </div>
    );
  }

  const pulseGroups = {};
  const loose = [];
  for (const m of memories) {
    if (m.type === "pulse_summary" && m.pulse_number != null) {
      pulseGroups[m.pulse_number] = pulseGroups[m.pulse_number] || { summary: null, principles: [] };
      pulseGroups[m.pulse_number].summary = m;
    } else if (m.pulse_number != null) {
      pulseGroups[m.pulse_number] = pulseGroups[m.pulse_number] || { summary: null, principles: [] };
      pulseGroups[m.pulse_number].principles.push(m);
    } else {
      loose.push(m);
    }
  }
  const pulseKeys = Object.keys(pulseGroups).map(Number).sort((a, b) => b - a);

  return (
    <section data-testid="vault-memories-panel" className="border border-zinc-800 bg-[#0b0b0d]">
      <div className="flex items-center justify-between p-4 border-b border-zinc-900">
        <div className="flex items-center gap-3">
          <Brain className="h-4 w-4 text-emerald-400" />
          <div>
            <div className="font-heading text-lg">Archive of Remembrance</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {memories.length} memories · surgical forget enabled
            </div>
          </div>
        </div>
      </div>

      {memories.length === 0 ? (
        <div className="p-6 text-[11px] uppercase tracking-[0.3em] text-zinc-600">
          (empty — no docking summaries yet)
        </div>
      ) : (
        <div className="divide-y divide-zinc-900">
          {pulseKeys.map((pk) => {
            const grp = pulseGroups[pk];
            const key = `pulse-${pk}`;
            const open = !!expanded[key];
            return (
              <div key={key} data-testid={`vault-pulse-${pk}`}>
                <div className="flex items-center justify-between gap-3 p-3">
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [key]: !open }))}
                    className="flex items-center gap-3 text-left min-w-0 flex-1"
                  >
                    {open ? (
                      <ChevronDown className="h-4 w-4 text-emerald-400 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-xs text-zinc-100">
                        Pulse #{pk}
                        {grp.summary?.lifetime_dock_count != null && (
                          <span className="text-zinc-500 ml-2">
                            · lifetime #{grp.summary.lifetime_dock_count}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                        {(grp.summary ? 1 : 0) + grp.principles.length} memories
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => doForgetPulse(pk)}
                    disabled={busy === `pulse:${pk}`}
                    data-testid={`vault-forget-pulse-${pk}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-rose-500/40 text-rose-400 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 text-[10px] uppercase tracking-[0.3em]"
                  >
                    {busy === `pulse:${pk}` ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                    Forget pulse
                  </button>
                </div>
                {open && (
                  <div className="bg-[#07070a] border-t border-zinc-900 p-3 space-y-2">
                    {grp.summary && (
                      <MemoryRow m={grp.summary} busy={busy} onForget={doForget} />
                    )}
                    {grp.principles.map((m) => (
                      <MemoryRow key={m.id} m={m} busy={busy} onForget={doForget} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {loose.length > 0 && (
            <div className="p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                Unpaired memories
              </div>
              {loose.map((m) => (
                <MemoryRow key={m.id} m={m} busy={busy} onForget={doForget} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function MemoryRow({ m, busy, onForget }) {
  return (
    <div
      data-testid={`vault-memory-${m.id}`}
      className="border border-zinc-800 bg-zinc-950 p-2 text-[11px]"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.3em] text-emerald-400 min-w-0">
          <Database className="h-3 w-3 shrink-0" />
          <span>{m.type}</span>
          <span className="text-zinc-500 truncate">{fmtTime(m.timestamp)}</span>
        </div>
        <button
          type="button"
          onClick={() => onForget(m.id)}
          disabled={busy === m.id}
          data-testid={`vault-forget-memory-${m.id}`}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-rose-500/40 text-rose-400 hover:bg-rose-500 hover:text-zinc-950 disabled:opacity-50 text-[9px] uppercase tracking-[0.3em]"
        >
          {busy === m.id ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Trash2 className="h-3 w-3" />
          )}
          Forget
        </button>
      </div>
      <div className="text-zinc-400 whitespace-pre-wrap">{m.snippet}</div>
    </div>
  );
}


function SafetyPanel() {
  const [data, setData] = useState(null);
  const [win, setWin] = useState("week");
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      setData(await safetyTelemetry());
      setLastRefresh(new Date());
    } catch (err) { console.warn("[Vault] safety telemetry failed:", err); }
    finally { setRefreshing(false); }
  };
  useEffect(() => {
    load();
    // Auto-refresh every 30s while the Vault is open. Pauses when the tab
    // is hidden so we don't burn cycles on a background window.
    const tick = () => {
      if (!document.hidden) load();
    };
    const id = setInterval(tick, 30_000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) {
    return (
      <div className="border border-zinc-800 bg-[#0b0b0d] p-6 text-[11px] uppercase tracking-[0.3em] text-zinc-500">
        <ShieldCheck className="inline h-3 w-3 mr-2" /> loading safety telemetry…
      </div>
    );
  }

  const w = data.windows[win] || {};
  const c = data.constitutional || {};
  const windows = [
    { key: "day", label: "24h" },
    { key: "week", label: "7 days" },
    { key: "all_time", label: "All time" },
  ];

  const blockRate = w.total_chats > 0
    ? ((w.anchor_blocked / w.total_chats) * 100).toFixed(1)
    : "0.0";
  const driftRate = w.total_chats > 0
    ? ((w.drift_detected / w.total_chats) * 100).toFixed(1)
    : "0.0";

  return (
    <section
      data-testid="vault-safety-panel"
      className="border border-zinc-800 bg-[#0b0b0d]"
    >
      <div className="flex items-center justify-between p-4 border-b border-zinc-900 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          <div>
            <div className="font-heading text-lg">Safety Telemetry</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
              <span>live pulse · auto 30s</span>
              {lastRefresh && (
                <span
                  data-testid="safety-last-refresh"
                  className="text-zinc-600 normal-case tracking-normal"
                >
                  · {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {windows.map((x) => (
            <button
              key={x.key}
              type="button"
              onClick={() => setWin(x.key)}
              data-testid={`safety-window-${x.key}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] border ${
                win === x.key
                  ? "border-emerald-500 bg-emerald-500/10 text-emerald-400"
                  : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300"
              }`}
            >
              {x.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            data-testid="safety-refresh"
            className="ml-2 p-1.5 text-zinc-500 hover:text-zinc-200 disabled:opacity-50"
            title="Refresh"
          >
            <Activity className={`h-3.5 w-3.5 ${refreshing ? "animate-pulse" : ""}`} />
          </button>
        </div>
      </div>

      {/* Top row stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3">
        <SafetyStat label="Total chats" value={w.total_chats ?? 0} icon={Activity} />
        <SafetyStat
          label="Blocked (L0/L3)"
          value={w.anchor_blocked ?? 0}
          icon={ShieldOff}
          sub={`${blockRate}%`}
        />
        <SafetyStat
          label="Drift rewrites (L4-6)"
          value={w.drift_detected ?? 0}
          icon={AlertTriangle}
          sub={`${driftRate}%`}
        />
        <SafetyStat label="Pulses (L7)" value={w.pulses ?? 0} icon={Heart} />
        <SafetyStat
          label="Safe Mode users"
          value={c.safe_mode_users ?? 0}
          icon={ShieldCheck}
          alert={c.safe_mode_users > 0}
        />
      </div>

      {/* 14-day trend */}
      {Array.isArray(data.trend_14d) && data.trend_14d.length > 0 && (
        <TrendChart days={data.trend_14d} />
      )}

      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-900 border-t border-zinc-900">
        {/* By layer */}
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            Blocks by layer
          </div>
          {Object.keys(w.by_layer || {}).length === 0 ? (
            <div className="text-[11px] text-zinc-600 uppercase tracking-[0.25em]">
              no blocks in this window
            </div>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(w.by_layer || {}).map(([layer, n]) => (
                <div
                  key={layer}
                  className="flex items-center justify-between text-[11px]"
                  data-testid={`safety-by-layer-${layer}`}
                >
                  <span className="text-zinc-300">{layer}</span>
                  <span className="font-heading tabular-nums text-emerald-400">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top drift flags */}
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            Top drift flags
          </div>
          {(w.top_drift_flags || []).length === 0 ? (
            <div className="text-[11px] text-zinc-600 uppercase tracking-[0.25em]">
              no drift rewrites in this window
            </div>
          ) : (
            <div className="space-y-1.5">
              {(w.top_drift_flags || []).map((f) => (
                <div
                  key={f.flag}
                  className="flex items-center justify-between text-[11px]"
                  data-testid={`safety-drift-flag-${f.flag}`}
                >
                  <span className="text-zinc-300">{f.flag}</span>
                  <span className="font-heading tabular-nums text-zinc-400">{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Constitutional footers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 border-t border-zinc-900 text-[11px]">
        <MiniStat label="Aegis violations" value={c.aegis_violations_total ?? 0} />
        <MiniStat label="Failed handshakes" value={c.layer7_denials_total ?? 0} />
        <MiniStat label="Vault denials" value={c.blackbox_denials_total ?? 0} />
        <MiniStat label="Neutralizations" value={c.drift_neutralizations_total ?? 0} />
      </div>
    </section>
  );
}

function SafetyStat({ label, value, icon: Icon, sub, alert }) {
  return (
    <div
      className={`border p-3 ${
        alert ? "border-rose-500/50 bg-rose-500/5" : "border-zinc-800 bg-[#07070a]"
      }`}
    >
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`font-heading text-xl tabular-nums ${alert ? "text-rose-400" : "text-zinc-100"}`}>
          {value}
        </span>
        {sub && (
          <span className="text-[10px] text-zinc-500 tabular-nums">{sub}</span>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="flex items-center justify-between text-zinc-400">
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</span>
      <span className="font-heading tabular-nums text-zinc-200">{value}</span>
    </div>
  );
}


const TREND_METRICS = [
  { key: "total_chats", label: "Chats", color: "bg-emerald-500/70", text: "text-emerald-400" },
  { key: "anchor_blocked", label: "Anchor blocks", color: "bg-amber-500/70", text: "text-amber-400" },
  { key: "drift_detected", label: "Drift rewrites", color: "bg-sky-500/70", text: "text-sky-400" },
  { key: "pulses", label: "Pulses", color: "bg-rose-500/70", text: "text-rose-400" },
];

function TrendChart({ days }) {
  const [metric, setMetric] = useState("total_chats");
  const values = days.map((d) => Number(d[metric]) || 0);
  const peak = Math.max(1, ...values);
  const totalWindow = values.reduce((a, b) => a + b, 0);
  const active = TREND_METRICS.find((m) => m.key === metric) || TREND_METRICS[0];
  const fmtDay = (iso) => {
    try {
      const d = new Date(`${iso}T00:00:00Z`);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return iso; }
  };

  return (
    <div
      className="border-t border-zinc-900 p-4"
      data-testid="safety-trend-14d"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            14-day trend
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5 font-body">
            <span className={`font-heading tabular-nums ${active.text}`}>{totalWindow}</span>{" "}
            <span className="text-zinc-500">{active.label.toLowerCase()} · peak {peak}/day</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="safety-trend-metric-group">
          {TREND_METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetric(m.key)}
              data-testid={`safety-trend-metric-${m.key}`}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                metric === m.key
                  ? "border-zinc-500 text-zinc-100 bg-zinc-900"
                  : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="flex items-stretch gap-1 h-24 pt-2 border-t border-zinc-900"
        data-testid="safety-trend-bars"
      >
        {days.map((d, i) => {
          const v = values[i];
          const pct = Math.round((v / peak) * 100);
          return (
            <div
              key={d.date}
              className="relative flex-1 flex flex-col justify-end group"
              data-testid={`safety-trend-day-${i}`}
              title={`${d.date} · ${v} ${active.label.toLowerCase()}`}
            >
              <div
                className={`w-full ${active.color} ${v === 0 ? "opacity-20" : "opacity-100"} transition-all`}
                style={{ height: `${Math.max(pct, v > 0 ? 6 : 3)}%` }}
              />
              {v > 0 && (
                <div className={`absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] tabular-nums ${active.text} opacity-0 group-hover:opacity-100 transition-opacity`}>
                  {v}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-[9px] uppercase tracking-[0.2em] text-zinc-600 tabular-nums">
        <span>{fmtDay(days[0].date)}</span>
        <span>{fmtDay(days[Math.floor(days.length / 2)].date)}</span>
        <span>{fmtDay(days[days.length - 1].date)}</span>
      </div>
    </div>
  );
}

